import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project, ActivityLogEntry, HealthRAG } from '../../../types';
import { getProjectActivity, relativeTime } from '../../../services/activityService';
import { updateProject } from '../../../services/projectService';
import PunchListWidget from '../../../components/dashboard/PunchListWidget';
import { TabKey } from '../ProjectNavBar';

interface Props {
  pca: IPublicClientApplication;
  project: Project;
  onNavigate?: (tab: TabKey) => void;
  onProjectChanged?: (patch: Partial<Project>) => void;
}

const HEALTH_LABEL: Record<number, string> = {
  [HealthRAG.Green]: 'On Track', [HealthRAG.Amber]: 'At Risk', [HealthRAG.Red]: 'Off Track',
};
const HEALTH_COLOR: Record<number, string> = {
  [HealthRAG.Green]: 'var(--success)', [HealthRAG.Amber]: 'var(--warning)', [HealthRAG.Red]: 'var(--danger)',
};

function Stat({ label, value, color = 'var(--brand)' }: { label: string; value: string | number; color?: string }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 200, fontSize: 32, color, lineHeight: 1, marginTop: 6 }}>
        {value}
      </div>
    </div>
  );
}

export default function OverviewTab({ pca, project, onNavigate, onProjectChanged }: Props) {
  const [activity, setActivity] = useState<ActivityLogEntry[]>([]);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(project.dagrun_pm_description ?? '');
  const [savingDesc, setSavingDesc] = useState(false);
  const [descError, setDescError] = useState<string | null>(null);

  useEffect(() => {
    getProjectActivity(pca, project.dagrun_pm_projectid, 5).then(setActivity).catch(() => {});
  }, [pca, project.dagrun_pm_projectid]);

  const startEditDesc = () => {
    setDescDraft(project.dagrun_pm_description ?? '');
    setDescError(null);
    setEditingDesc(true);
  };

  const saveDesc = async () => {
    const next = descDraft.trim();
    if (next === (project.dagrun_pm_description ?? '')) { setEditingDesc(false); return; }
    setSavingDesc(true);
    setDescError(null);
    try {
      await updateProject(pca, project.dagrun_pm_projectid, { dagrun_pm_description: next || null });
      onProjectChanged?.({ dagrun_pm_description: next || null });
      setEditingDesc(false);
    } catch (e) {
      setDescError(e instanceof Error ? e.message : 'Failed to save description.');
    } finally {
      setSavingDesc(false);
    }
  };

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        {/* Left column — description + stats */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div className="eyebrow">ABOUT THIS PROJECT</div>
            {!editingDesc && (
              <button
                type="button"
                onClick={startEditDesc}
                title="Edit description"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--brand)', fontSize: 12, fontWeight: 600,
                  fontFamily: 'var(--font-ui)', padding: '2px 4px',
                }}
              >Edit</button>
            )}
          </div>
          {editingDesc ? (
            <div style={{ marginTop: 8 }}>
              <textarea
                autoFocus
                value={descDraft}
                onChange={e => setDescDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') { setEditingDesc(false); } }}
                rows={4}
                placeholder="Describe this project…"
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'vertical',
                  minHeight: 90, fontSize: 14, lineHeight: 1.6,
                  fontFamily: 'var(--font-ui)', color: 'var(--ink-2)',
                  border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                  padding: '10px 12px',
                }}
              />
              {descError && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{descError}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn-secondary" onClick={() => setEditingDesc(false)} disabled={savingDesc}>Cancel</button>
                <button className="btn-primary" onClick={saveDesc} disabled={savingDesc}>{savingDesc ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          ) : (
            <p
              onClick={startEditDesc}
              title="Click to edit"
              style={{ fontWeight: 300, fontSize: 14, color: project.dagrun_pm_description ? 'var(--ink-2)' : 'var(--ink-4)', lineHeight: 1.6, marginTop: 8, cursor: 'pointer' }}
            >
              {project.dagrun_pm_description || 'No description set yet — click to add one.'}
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-light)' }}>
            <Stat label="COMPLETE" value={`${project.dagrun_pm_percentcomplete ?? 0}%`} />
            <Stat label="OPEN ISSUES" value={project.dagrun_pm_openissuescount ?? 0} />
            <Stat label="OPEN RISKS" value={project.dagrun_pm_openriskscount ?? 0} />
            <Stat label="NEXT MILESTONE" value={
              project.dagrun_pm_nextmilestonedate
                ? new Date(project.dagrun_pm_nextmilestonedate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                : '—'
            } />
          </div>
        </div>

        {/* Right column — health summary */}
        <div className="card" style={{ padding: 20 }}>
          <div className="eyebrow">HEALTH</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <span style={{
              width: 14, height: 14, borderRadius: 7,
              background: project.dagrun_pm_healthrag != null
                ? HEALTH_COLOR[project.dagrun_pm_healthrag] ?? 'var(--ink-4)'
                : 'var(--ink-5)',
            }} />
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>
              {project.dagrun_pm_healthrag != null ? HEALTH_LABEL[project.dagrun_pm_healthrag] ?? '—' : 'Not set'}
            </span>
          </div>
          <div style={{ marginTop: 20 }}>
            <div className="eyebrow">COMPLETION</div>
            <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 6, overflow: 'hidden', marginTop: 8 }}>
              <div style={{ height: '100%', width: `${project.dagrun_pm_percentcomplete ?? 0}%`, background: 'var(--brand)' }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
              {project.dagrun_pm_percentcomplete ?? 0}% / 100%
            </div>
          </div>
        </div>
      </div>

      {/* Module widgets — render only for enabled modules. */}
      {project.dagrun_pm_module_punchlist && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 16 }}>
          <PunchListWidget pca={pca} project={project} onOpen={() => onNavigate?.('punchlist')} />
        </div>
      )}

      {/* Recent activity timeline */}
      <div style={{ marginTop: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>RECENT ACTIVITY</div>
        {activity.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center', fontWeight: 300, fontSize: 15, color: 'var(--ink-3)' }}>
            No activity yet.
          </div>
        ) : (
          <div>
            {activity.map(a => {
              const actor = a.dagrun_pm_actorisai
                ? 'AI Agent'
                : (a.dagrun_pm_actorname ?? (a.dagrun_pm_actorid ? 'A user' : 'System'));
              const detail = a.dagrun_pm_actiondescription ?? a.dagrun_pm_name;
              return (
                <div key={a.dagrun_pm_activitylogid} style={{
                  borderLeft: '2px solid var(--brand-mist)',
                  paddingLeft: 16, marginBottom: 16,
                }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-4)' }}>
                    {relativeTime(a.createdon)}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 300, marginTop: 2 }}>
                    <span style={{ fontWeight: 600, color: 'var(--ink-2)' }}>{actor}</span>{' '}
                    <span>{a.dagrun_pm_actiontype ?? 'updated'}</span>
                    {a.dagrun_pm_entitytitle && (
                      <> <span style={{ fontWeight: 500 }}>{a.dagrun_pm_entitytitle}</span></>
                    )}
                    {detail && detail !== (a.dagrun_pm_actiontype ?? '') && (
                      <span style={{ color: 'var(--ink-3)' }}> &mdash; {detail}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
